import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Zap, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppFooter } from "@/components/app/AppFooter";

const BG = "https://images.unsplash.com/photo-1667964267136-c0256d70b7a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  // No pre-filled credentials: this screen ships to the public internet.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-brand-bg">
      {/* Left: form */}
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-[46%] lg:px-20">
        <div className="mx-auto w-full max-w-sm animate-fade-up">
          <div className="mb-10 flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-red text-white">
              <Zap className="h-5 w-5" fill="currentColor" />
            </div>
            <div className="leading-tight">
              <p className="font-heading text-xl font-extrabold tracking-tight text-brand-ink">TL-Thunderbird</p>
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-brand-mute">
                Campaign Operations
              </p>
            </div>
          </div>

          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-brand-ink sm:text-4xl">
            Run a sharper race.
          </h1>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="h-11 w-full bg-brand-red text-white hover:bg-brand-redhover active:scale-[0.98]"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="ml-1 h-4 w-4" /></>}
            </Button>
          </form>

          <p className="mt-6 text-xs leading-relaxed text-brand-mute">
            Accounts are created by a campaign administrator. Need access? Ask your administrator
            to add you in <span className="font-medium text-brand-ink">Settings &gt; Team</span>.
          </p>

          <AppFooter className="mt-10" />
        </div>
      </div>

      {/* Right: imagery */}
      <div className="relative hidden flex-1 lg:block">
        <img src={BG} alt="Voters at a polling place" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-navy/85 via-brand-navy/55 to-brand-red/35" />
      </div>
    </div>
  );
}
